import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { TakeoffService } from '../services/TakeoffService';
import './NewTakeoffModal.css';
import { useTrackModal } from '../hooks/useTrackModal';

const NewTakeoffModal = ({ isOpen, onClose, onTakeoffCreated }) => {
  useTrackModal("NewTakeoffModal", isOpen);
  const [formData, setFormData] = useState({
    project_name: '',
    job_number: '',
    status: 'Working on it',
    ufredo_price: '',
    ab_civil_price: '',
    completion_needed_date: '',
    client_id: '',
    contact_id: '',
    email: '',
    form_phone: '',
    form_name: '',
    other_request: '',
    scopes_needed: [],
    top_soil_replacement_depth: '',
    top_soil_stripping_depth: '',
    cad_file_available: '',
    initial_acceptance: '',
    form_client: '',
    invoiced_status: '',
    invoiced_date: '',
    paid_date: '',
    earthworks_tech_inv_number: '',
    paid_to_ufredo: '',
    paid_to_ufredo_date: ''
  });

  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadClients();
      loadContacts();
      // Reset form when modal opens
      setFormData({
        project_name: '',
        job_number: '',
        status: 'Working on it',
        ufredo_price: '',
        ab_civil_price: '',
        completion_needed_date: '',
        client_id: '',
        contact_id: '',
        email: '',
        form_phone: '',
        form_name: '',
        other_request: '',
        scopes_needed: [],
        top_soil_replacement_depth: '',
        top_soil_stripping_depth: '',
        cad_file_available: '',
        initial_acceptance: '',
        form_client: '',
        invoiced_status: '',
        invoiced_date: '',
        paid_date: '',
        earthworks_tech_inv_number: '',
        paid_to_ufredo: '',
        paid_to_ufredo_date: ''
      });
      setError('');
    }
  }, [isOpen]);

  const loadClients = async () => {
    try {
      const clientsData = await TakeoffService.getClients();
      setClients(clientsData);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const loadContacts = async () => {
    try {
      const contactsData = await TakeoffService.getContacts();
      setContacts(contactsData);
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Clear contact when client changes
      if (name === 'client_id') {
        newData.contact_id = '';
      }
      
      return newData;
    });
  };

  // Filter contacts by selected client
  const getFilteredContacts = () => {
    if (!formData.client_id) return contacts;
    return contacts.filter(contact => contact.client_id === parseInt(formData.client_id));
  };

  // Handle scopes needed multiple select
  const handleScopesChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      scopes_needed: selectedOptions
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Convert price fields to numbers if they have values
        ufredo_price: formData.ufredo_price ? parseFloat(formData.ufredo_price.replace(/[$,]/g, '')) : null,
        ab_civil_price: formData.ab_civil_price ? parseFloat(formData.ab_civil_price.replace(/[$,]/g, '')) : null,
        // Convert client_id to integer if selected
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        // Convert contact_id to integer if selected
        contact_id: formData.contact_id ? parseInt(formData.contact_id) : null,
        // Ensure dates are in correct format or null
        completion_needed_date: formData.completion_needed_date || null,
        invoiced_date: formData.invoiced_date || null,
        paid_date: formData.paid_date || null,
        paid_to_ufredo_date: formData.paid_to_ufredo_date || null
      };

      // Remove empty string values
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      const newTakeoff = await TakeoffService.createTakeoff(submitData);
      onTakeoffCreated(newTakeoff);
      onClose();
    } catch (err) {
      console.error('Error creating takeoff:', err);
      setError(err.message || 'Failed to create takeoff');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content new-takeoff-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Takeoff</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="takeoff-form">
          <div className="form-grid">
            {/* LEFT COLUMN */}
            <div className="left-column">
              {/* Project Information */}
              <div className="form-section">
                <h3>Project Information</h3>
                
                <div className="form-group">
                  <label htmlFor="project_name">Project Name *</label>
                  <input
                    type="text"
                    id="project_name"
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter project name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="job_number">Job Number *</label>
                  <input
                    type="text"
                    id="job_number"
                    name="job_number"
                    value={formData.job_number}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 12345"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="Working on it">Working on it</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="completion_needed_date">Due Date</label>
                  <input
                    type="date"
                    id="completion_needed_date"
                    name="completion_needed_date"
                    value={formData.completion_needed_date}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Client & Contact */}
              <div className="form-section">
                <h3>Client & Contact</h3>
                
                <div className="form-group">
                  <label htmlFor="client_id">Client</label>
                  <select
                    id="client_id"
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleChange}
                  >
                    <option value="">Select a client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_id">Contact Person</label>
                  <select
                    id="contact_id"
                    name="contact_id"
                    value={formData.contact_id}
                    onChange={handleChange}
                    disabled={!formData.client_id}
                  >
                    <option value="">Select a contact</option>
                    {getFilteredContacts().map(contact => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="form_phone">Phone</label>
                  <input
                    type="tel"
                    id="form_phone"
                    name="form_phone"
                    value={formData.form_phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Form Information */}
              <div className="form-section">
                <h3>Form Information</h3>
                
                <div className="form-group">
                  <label htmlFor="form_name">Form Name</label>
                  <input
                    type="text"
                    id="form_name"
                    name="form_name"
                    value={formData.form_name}
                    onChange={handleChange}
                    placeholder="Contact name from form"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="form_client">Form Client</label>
                  <input
                    type="text"
                    id="form_client"
                    name="form_client"
                    value={formData.form_client}
                    onChange={handleChange}
                    placeholder="Client name from form"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cad_file_available">CAD File Available</label>
                  <select
                    id="cad_file_available"
                    name="cad_file_available"
                    value={formData.cad_file_available}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="right-column">
              {/* Technical Requirements */}
              <div className="form-section">
                <h3>Technical Requirements</h3>
                
                <div className="form-group">
                  <label htmlFor="other_request">Additional Request</label>
                  <textarea
                    id="other_request"
                    name="other_request"
                    value={formData.other_request}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Any additional requests or notes"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="scopes_needed">Scopes Needed</label>
                  <select
                    id="scopes_needed"
                    name="scopes_needed"
                    multiple
                    value={formData.scopes_needed || []}
                    onChange={handleScopesChange}
                    className="multiple-select"
                  >
                    <option value="Utilities">Utilities</option>
                    <option value="Storm">Storm</option>
                    <option value="Water">Water</option>
                    <option value="Sewer">Sewer</option>
                    <option value="Road">Road</option>
                    <option value="Grading">Grading</option>
                    <option value="Excavation">Excavation</option>
                    <option value="Paving">Paving</option>
                    <option value="Concrete">Concrete</option>
                    <option value="Landscaping">Landscaping</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Gas">Gas</option>
                  </select>
                  <small className="field-help">Hold Ctrl/Cmd to select multiple options</small>
                </div>
              </div>

              {/* Soil Specifications */}
              <div className="form-section">
                <h3>Soil Specifications</h3>
                
                <div className="form-group">
                  <label htmlFor="top_soil_stripping_depth">Top Soil Stripping Depth</label>
                  <input
                    type="text"
                    id="top_soil_stripping_depth"
                    name="top_soil_stripping_depth"
                    value={formData.top_soil_stripping_depth}
                    onChange={handleChange}
                    placeholder="e.g., 4 inches"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="top_soil_replacement_depth">Top Soil Replacement Depth</label>
                  <input
                    type="text"
                    id="top_soil_replacement_depth"
                    name="top_soil_replacement_depth"
                    value={formData.top_soil_replacement_depth}
                    onChange={handleChange}
                    placeholder="e.g., 6 inches"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="form-section">
                <h3>Pricing</h3>
                
                <div className="form-group">
                  <label htmlFor="ufredo_price">Ufredo Price</label>
                  <input
                    type="text"
                    id="ufredo_price"
                    name="ufredo_price"
                    value={formData.ufredo_price}
                    onChange={handleChange}
                    placeholder="$0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ab_civil_price">AB Civil Price</label>
                  <input
                    type="text"
                    id="ab_civil_price"
                    name="ab_civil_price"
                    value={formData.ab_civil_price}
                    onChange={handleChange}
                    placeholder="$0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="initial_acceptance">Initial Acceptance</label>
                  <input
                    type="text"
                    id="initial_acceptance"
                    name="initial_acceptance"
                    value={formData.initial_acceptance}
                    onChange={handleChange}
                    placeholder="Initial acceptance status"
                  />
                </div>
              </div>

              {/* Invoice & Payment (Optional) */}
              <div className="form-section">
                <h3>Invoice & Payment (Optional)</h3>
                
                <div className="form-group">
                  <label htmlFor="invoiced_status">Invoiced Status</label>
                  <select
                    id="invoiced_status"
                    name="invoiced_status"
                    value={formData.invoiced_status}
                    onChange={handleChange}
                  >
                    <option value="">None</option>
                    <option value="Invoiced">Invoiced</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="paid_to_ufredo">Paid to Ufredo</label>
                  <select
                    id="paid_to_ufredo"
                    name="paid_to_ufredo"
                    value={formData.paid_to_ufredo}
                    onChange={handleChange}
                  >
                    <option value="">Select Status</option>
                    <option value="No">No</option>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Takeoff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTakeoffModal;